import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useSiteContent } from "../context/SiteContentContext";
import { Button } from "@/components/ui/button";
import { Crown, Check, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { content } = useSiteContent();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [notified, setNotified] = useState(false);
  const [busy, setBusy] = useState(false);

  const tier = (content.subscription_tiers || []).find((t) => t.key === params.get("tier"));

  if (!tier) {
    return (
      <div className="max-w-lg mx-auto text-center py-16" data-testid="cart-empty">
        <p className="text-spotter-maroon mb-4">No membership selected.</p>
        <Button onClick={() => navigate("/subscribe")} className="bg-spotter-maroon text-spotter-cream border-2 border-black">View Memberships</Button>
      </div>
    );
  }

  const paypalHandle = content.pay_paypal_me;
  const venmoHandle = content.pay_venmo;
  const paypalUrl = paypalHandle ? `https://paypal.me/${paypalHandle}/${tier.price}` : null;
  const venmoUrl = venmoHandle ? `https://venmo.com/u/${venmoHandle}` : null;

  const confirmPaid = async () => {
    setBusy(true);
    try { await api.post("/subscribe/request", { tier: tier.key }); setNotified(true); toast.success("Thanks! An admin will activate your membership shortly."); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="cart-page">
      <button onClick={() => navigate("/subscribe")} className="flex items-center gap-1 text-spotter-maroon font-semibold mb-4"><ArrowLeft className="w-4 h-4" />Back to memberships</button>
      <div className="bg-white border-4 border-black rounded-2xl p-5 brand-shadow">
        <div className="flex items-center gap-2"><Crown className="w-7 h-7" style={{ color: "#E19C00" }} /><h1 className="font-display text-3xl font-bold text-spotter-maroon">{tier.name}</h1></div>
        <span className="text-xs font-bold uppercase tracking-wide text-spotter-maroon/60">{tier.badge}</span>
        <div className="my-3 pb-3 border-b-2 border-black"><span className="text-4xl font-display font-bold text-spotter-maroon">${tier.price}</span><span className="text-spotter-maroon">/{tier.period}</span></div>
        <ul className="space-y-1.5 mb-5">
          {(tier.features || []).map((f, i) => <li key={i} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 mt-0.5 shrink-0 text-spotter-gold" />{f}</li>)}
        </ul>
        <p className="text-sm font-bold text-spotter-maroon mb-2">Pay ${tier.price} securely, then tap "I've paid":</p>
        <div className="space-y-2 flex flex-col items-center">
          {paypalUrl && <a data-testid="cart-paypal" href={paypalUrl} target="_blank" rel="noopener noreferrer" className="block w-full text-center text-white font-bold rounded-lg px-4 py-3 border-2 border-black hover:opacity-90" style={{ background: "#520202" }}>Pay with PayPal — ${tier.price}</a>}
          {venmoUrl && <a data-testid="cart-venmo" href={venmoUrl} target="_blank" rel="noopener noreferrer" className="block w-full text-center text-white font-bold rounded-lg px-4 py-3 border-2 border-black hover:opacity-90" style={{ background: "#E19C00" }}>Pay with Venmo — @{venmoHandle}</a>}
          {venmoUrl && <p className="text-xs text-spotter-maroon/70 text-center">On Venmo, please send exactly <b>${tier.price}</b> and note your Spotter username &amp; tier.</p>}
        </div>
        <div className="mt-5 pt-4 border-t-2 border-black">
          {notified ? (
            <div className="flex items-center gap-2 text-spotter-maroon font-semibold" data-testid="cart-confirmed"><ShieldCheck className="w-5 h-5" />Payment reported — awaiting admin activation.</div>
          ) : (
            <Button data-testid="cart-confirm-paid" onClick={confirmPaid} disabled={busy} className="w-full bg-spotter-maroon text-spotter-cream font-heading font-bold border-2 border-black">
              {busy ? "Sending…" : "I've completed payment"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
